import { BaseService } from './base.service';
import { User } from '../models/user.models';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export type SearchEquipmentOptions = {
  brand?: string;
  equipment_type_id?: number;
  equipment_subtype_id?: number;
};

export interface EquipmentSearchSummary {
  total_owners: number;
  total_equipment: number;
  count_owned: number;
  count_managed: number;
  count_skis: number;
  count_shoes: number;
  count_other: number;
  athlete_count: number;
}

export const EMPTY_EQUIPMENT_SEARCH_SUMMARY: EquipmentSearchSummary = {
  total_owners: 0,
  total_equipment: 0,
  count_owned: 0,
  count_managed: 0,
  count_skis: 0,
  count_shoes: 0,
  count_other: 0,
  athlete_count: 0,
};

export default class EquipmentService extends BaseService {
  public async getEquipments(member_id?: number) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.get(`${this.backendUrl}/v2/equipments`, {
      params: { ...(member_id != null && { member_id }) },
      headers: {
        'Time-Zone': dayjs.tz.guess(),
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async searchEquipment(q = '', options?: SearchEquipmentOptions) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params: Record<string, string | number | boolean> = { q };
    if (options?.brand) params.brand = options.brand;
    if (options?.equipment_type_id !== undefined) params.equipment_type_id = options.equipment_type_id;
    if (options?.equipment_subtype_id !== undefined) params.equipment_subtype_id = options.equipment_subtype_id;
    return this.axios.get(`${this.backendUrl}/v2/equipments/search`, {
      params,
      headers: {
        'Time-Zone': dayjs.tz.guess(),
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async searchTeamEquipment(teamId: number, q = '', options?: SearchEquipmentOptions) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    const params: Record<string, string | number | boolean> = { q };
    if (options?.brand) params.brand = options.brand;
    if (options?.equipment_type_id !== undefined) params.equipment_type_id = options.equipment_type_id;
    if (options?.equipment_subtype_id !== undefined) params.equipment_subtype_id = options.equipment_subtype_id;
    return this.axios.get(`${this.backendUrl}/v2/team/${teamId}/equipments/search`, {
      params,
      headers: {
        'Time-Zone': dayjs.tz.guess(),
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async getManagedOwners(teamId?: number) {
    return this.axios.get(`${this.backendUrl}/v2/equipments/managed-owners`, {
      params: { ...(teamId != null && { team: teamId }) },
    });
  }

  public async getEquipment(id: number) {
    return this.axios.get(`${this.backendUrl}/v2/equipment/${id}`, {});
  }

  public async createEquipment(name: string, description: string) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);

    return this.axios.post(`${this.backendUrl}/v2/equipments`, formData, {});
  }

  public async updateEquipment(id: number, name: string, description: string) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);

    return this.axios.put(`${this.backendUrl}/equipment/${id}`, formData, {});
  }

  public async deleteEquipment(id: number) {
    return this.axios.delete(`${this.backendUrl}/v2/equipment/${id}`, {});
  }

  public async getEquipmentTypes() {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.get(`${this.backendUrl}/v2/equipment-types`, {
      headers: {
        'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  public async createEquipmentV2(payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(`${this.backendUrl}/v2/equipments`, payload, {
      headers: {
        'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  public async updateEquipmentV2(id: number, payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.put(`${this.backendUrl}/v2/equipment/${id}`, payload, {
      headers: {
        'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  public async patchEquipmentV2(id: number, payload: any) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.patch(`${this.backendUrl}/v2/equipment/${id}`, payload, {
      headers: {
        'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  public async publishEquipmentV2(id: number, published: boolean) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(
      `${this.backendUrl}/v2/equipment/${id}`,
      { published: published },
      {
        headers: {
          'x-access-token': user.token,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );
  }

  /**
   * Fetch public equipment by public_id (e.g. URL /equip/71062675).
   */
  public async getPublicEquipment(publicId: string) {
    return this.axios.get(`${this.backendUrl}/v2/equipment/public`, {
      params: { public_id: publicId },
      headers: {
        // 'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  public async getEquipmentNotes(equipmentId: number) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.get(`${this.backendUrl}/v2/equipment/${equipmentId}/notes`, {
      headers: {
        'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  public async createEquipmentNote(equipmentId: number, message: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(
      `${this.backendUrl}/v2/equipment/${equipmentId}/notes`,
      {
        message,
      },
      {
        headers: {
          'x-access-token': user.token,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );
  }

  public async updateEquipmentNote(equipmentId: number, noteId: number, message: string) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.put(
      `${this.backendUrl}/v2/equipment/${equipmentId}/note/${noteId}`,
      { message },
      {
        headers: {
          'x-access-token': user.token,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );
  }

  public async deleteEquipmentNote(equipmentId: number, noteId: number) {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.delete(`${this.backendUrl}/v2/equipment/${equipmentId}/note/${noteId}`, {
      headers: {
        'x-access-token': user.token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }
}
